/**
 * Issue #767 (epic #749, wave 6) — the coach's squad, grouped by role.
 *
 * `#716` (held) is why this file exists at all: the app has no single,
 * shared list of playing-role values. `PlayerProfile.jsx:443-447` writes
 * one of five strings (Batter, Bowler, Batting All-rounder, Bowling
 * All-rounder, Wicket-keeper); the seed (`api/src/db/seed.ts:466`) writes a
 * sixth ("All-rounder") that no UI picker offers. Decision 3 (2026-09-18)
 * fixed the FOUR labels a coach's squad groups under — Batter, All-rounder,
 * Bowler, Keeper — without touching the five/six-value field itself, so
 * this module's whole job is the many-to-four mapping, plus a fifth bucket
 * ("Role not set") for anything it does not recognise. No player is ever
 * dropped from the squad because of an unmapped or missing role string
 * (brief-767.md, issue #741 § 2).
 *
 * Deliberately a plain module of pure functions, no React — this repo's own
 * convention for a fact a unit test should pin without mounting anything
 * (`lib/recordFigures.js`, `lib/recordGate.js`).
 */

/** The fifth bucket — anything the mapping below does not recognise. */
export const ROLE_NOT_SET = "Role not set";

/** The four decided labels (decision 3, 2026-09-18), in the order the role
 * strip and the drawing's group list both use. `ROLE_NOT_SET` is last —
 * the drawing never shows it, but a real, unmapped value must still land
 * somewhere visible. */
export const ROLE_ORDER = ["Batter", "All-rounder", "Bowler", "Keeper", ROLE_NOT_SET];

/**
 * Every raw `playing_role` value the app can hold today, mapped to one of
 * the four decided labels. Covers both known sources: the five values
 * `PlayerProfile.jsx:443-447` offers, and the seed's own sixth
 * (`api/src/db/seed.ts:466`, "All-rounder", which no picker in the app
 * currently writes but which a seeded/demo account can already carry).
 */
const ROLE_MAP = {
  Batter: "Batter",
  Bowler: "Bowler",
  "Batting All-rounder": "All-rounder",
  "Bowling All-rounder": "All-rounder",
  "Wicket-keeper": "Keeper",
  "All-rounder": "All-rounder",
};

/**
 * Map one player's raw `playing_role` to one of the four decided labels,
 * or `ROLE_NOT_SET` when the value is missing or unrecognised. Never
 * throws, never drops the player — the caller always gets a bucket back.
 */
export function mapPlayingRole(rawRole) {
  if (!rawRole) return ROLE_NOT_SET;
  return ROLE_MAP[rawRole] || ROLE_NOT_SET;
}

/**
 * The GROUP header word for a role label (S7, ruling): "Batters",
 * "All-rounders", "Bowlers", "Keeper" (stays singular, as drawn), "Role not
 * set" (stays as-is — it already reads as a group name). Distinct from the
 * role-strip CHIP word, which is the singular label itself ("Batter",
 * `Squad.dc.html:95`), because the drawing uses the two forms differently:
 * "Batter 7" on the chip, "Batters" on the group header above the rows.
 */
const GROUP_LABELS = {
  Batter: "Batters",
  "All-rounder": "All-rounders",
  Bowler: "Bowlers",
  Keeper: "Keeper",
  [ROLE_NOT_SET]: ROLE_NOT_SET,
};

export function groupLabelFor(roleLabel) {
  return GROUP_LABELS[roleLabel] || roleLabel;
}

/**
 * Group a roster (`GET /players` rows, each carrying `playing_role`) by the
 * four decided labels plus `ROLE_NOT_SET`, in `ROLE_ORDER`. Every group is
 * present in the returned array, even at zero — the caller decides whether
 * an empty group renders (the drawing never shows one, and the fixture
 * this slice ships never produces one, but the function itself must not
 * assume that).
 *
 * @param {Array<{ player_id: string, playing_role?: string|null }>} players
 * @returns {Array<{ role: string, groupLabel: string, players: Array }>}
 */
export function groupByPlayingRole(players) {
  const buckets = new Map(ROLE_ORDER.map((role) => [role, []]));
  for (const p of players || []) {
    const role = mapPlayingRole(p?.playing_role);
    buckets.get(role).push(p);
  }
  return ROLE_ORDER.map((role) => ({ role, groupLabel: groupLabelFor(role), players: buckets.get(role) }));
}
